import { buildAuthenticatedHeaders } from './authStorage.ts';
import type { components } from './generated/api-types.ts';

export type MvpQuoteRecord = components['schemas']['MvpQuoteRecord'];
export type MvpQuoteLineRecord = components['schemas']['MvpQuoteLineRecord'];
export type MvpQuoteDetailRecord = components['schemas']['MvpQuoteDetailRecord'];
export type MvpWorkOrderRecord = components['schemas']['MvpWorkOrderRecord'];
export type MvpChecklistRecord = components['schemas']['MvpChecklistRecord'];
export type MvpProofRecord = components['schemas']['MvpProofRecord'];
export type MvpEventRecord = components['schemas']['MvpEventRecord'];
export type MvpReportRecord = components['schemas']['MvpReportRecord'];
export type MvpInvoiceRecord = components['schemas']['MvpInvoiceRecord'];
export type MvpWorkOrderDetailRecord = components['schemas']['MvpWorkOrderDetailRecord'];
export type MvpPilotState = components['schemas']['MvpPilotState'];
export type MvpQuoteLineUpdateInput = {
  label: string;
  qtyLabel: string;
  quantity: number;
  unitCents: number;
};

function getMvpApiBaseUrl(): string {
  const raw = String(import.meta.env.VITE_DEMO_API_BASE_URL || '/api').trim();
  if (!raw) return '/api';
  return raw.endsWith('/') ? raw.slice(0, -1) : raw;
}

async function requestMvpJson<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${getMvpApiBaseUrl()}${path}`, {
    credentials: 'include',
    ...init,
    headers: buildAuthenticatedHeaders({
      Accept: 'application/json',
      ...(init?.headers || {}),
    }),
  });

  const contentType = response.headers.get('content-type') || '';
  if (!response.ok) {
    let detail = response.statusText;
    if (contentType.includes('application/json')) {
      const payload = (await response.json()) as { detail?: string; error?: string };
      detail = payload.detail || payload.error || detail;
    } else {
      detail = (await response.text()) || detail;
    }
    throw new Error(`HTTP ${response.status}: ${detail}`);
  }
  if (!contentType.includes('application/json')) {
    throw new Error(`Type de contenu inattendu renvoyé par l’API MVP : ${contentType || 'inconnu'}`);
  }

  return (await response.json()) as T;
}

export async function fetchMvpQuotes(signal?: AbortSignal): Promise<MvpQuoteRecord[]> {
  return requestMvpJson<MvpQuoteRecord[]>('/mvp/quotes', { signal });
}

export async function fetchMvpQuoteDetail(quoteId: string, signal?: AbortSignal): Promise<MvpQuoteDetailRecord> {
  return requestMvpJson<MvpQuoteDetailRecord>(`/mvp/quotes/${encodeURIComponent(quoteId)}`, { signal });
}

export async function updateMvpQuoteLine(
  quoteId: string,
  lineId: string,
  payload: MvpQuoteLineUpdateInput,
): Promise<MvpQuoteDetailRecord> {
  return requestMvpJson<MvpQuoteDetailRecord>(`/mvp/quotes/${encodeURIComponent(quoteId)}/lines/${encodeURIComponent(lineId)}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });
}

export async function fetchMvpWorkOrders(signal?: AbortSignal): Promise<MvpWorkOrderRecord[]> {
  return requestMvpJson<MvpWorkOrderRecord[]>('/mvp/work-orders', { signal });
}

export async function fetchMvpWorkOrderDetail(workOrderId: string, signal?: AbortSignal): Promise<MvpWorkOrderDetailRecord> {
  return requestMvpJson<MvpWorkOrderDetailRecord>(`/mvp/work-orders/${encodeURIComponent(workOrderId)}`, { signal });
}

export async function fetchMvpPilotState(signal?: AbortSignal): Promise<MvpPilotState> {
  return requestMvpJson<MvpPilotState>('/mvp/pilot-state', { signal });
}
